/**
 * Submission Log tab.
 * Wave 1: one readable date format that sorts ('30 May 2025' + '488 d ago'), dates that need a look
 * pinned first with a one-step Fix, rows that identify the drawing and the sender, search + Project /
 * Discipline / Sent by / Date sent chips, a row opens the drawing.
 * Wave 2:
 *  - #1 submissions grouped into transmittals (same project, same day, same sender):
 *    "NOBU Residences #68 · 3 LCS drawings · sent 30 May 2025 by BW" and "488 d waiting";
 *  - #3 honest replies: what the Excel import filled in is "Awaiting reply" (drawing still Submitted)
 *    or "Not recorded" (drawing Blocked / In progress …), never a blanket "Approved"; a banner explains
 *    it and (Admins) stores the 169 as Awaiting reply in one step;
 *  - #4 "Awaiting reply | Replied | Not recorded | All" + waiting-time chips (90+ days …), oldest first;
 *  - #5 "Record reply" on each row / transmittal / selection: code A-D, reply date, comments;
 *  - #8 "Log transmittal": pick drawings, one submission for all of them.
 * Wave 3 (#9): "Chase N overdue" — one prepared follow-up email per project and recipient (ChaseDialog.js), a ✉
 * on each waiting row, the consultant turnaround panel (TurnaroundPanel.js), "From email" in Record reply (the AI
 * reads a pasted reply) and replies read from the shared mailbox waiting to be confirmed (ReplyInbox.js).
 * `readOnly` (BUG-002: Shop Drawings is View only for the role, or a Viewer account): the log to read and export —
 * no Record reply / Change / Reply to N, no Chase, no Log transmittal, no date Fix, no mailbox suggestions to
 * confirm, no "Mark as awaiting", no check boxes or bulk bar; ⋯ keeps the page's read items.
 */
import { useCallback, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpRight, CalendarClock, Download, FileText, Gauge, Mail, MailCheck, SearchX, Send, TriangleAlert, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RowActions } from '@/components/ui/row-actions';
import { Segmented } from '@/components/ui/segmented';
import { StatusPill } from '@/components/ui/status-pill';
import { cn } from '@/lib/utils';
import { formatDate, formatNumber } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { getStatusTone } from '@/lib/status';
import { readStored, storageKey, writeStored } from '@/lib/storage';
import { ChaseComposer, ChaseDialog, useChaseContacts } from './ChaseDialog';
import { ReplyInbox, useReplyInbox } from './ReplyInbox';
import { TurnaroundPanel } from './TurnaroundPanel';
import {
  AGE_BUCKETS,
  chaseGroups,
  REPLY_CODES,
  REPLY_CODE_OF,
  REPLY_LABELS,
  SENT_BUCKETS,
  ageBucket,
  displayPerson,
  drawingTitle,
  labelize,
  optionCounts,
  replyLabel,
  safeHref,
  toDayFirst,
  todayIso,
  transmittalInfo,
  transmittalNoun,
  valueKey,
} from './model';
import {
  AgeText,
  BulkBar,
  Callout,
  ChipSelect,
  DisciplineChip,
  DrawingIdText,
  GroupTitle,
  IconAction,
  PersonAvatar,
  SearchField,
  SubLineExtras,
  SubLinePart,
  TableFoot,
  subClass,
} from './SdUi';
import { RecordReplyPopover, ReplyCodeBadge } from './ReplyForm';

const fmt = (value) => formatNumber(value, { maxDecimals: 0 });
const DEFAULT_OPEN_ROWS = 30;
const BANNER_KEY = storageKey('sd', 'log', 'importBanner');

export const EMPTY_LOG_FILTERS = { search: '', project: null, disc: null, by: null, bucket: null, reply: null, age: null };

/** The reply view the tab shows: the chosen one, else "Awaiting reply" when anything waits, else All. */
export const replyViewOf = (filters, counts) => filters.reply || (counts.awaiting ? 'awaiting' : 'all');

function Dash() {
  // `relative`: the sr-only text is absolutely positioned — without a positioned box around it, it escapes the
  // table's sideways-scrolling wrapper on a phone and widens the whole page.
  return (
    <span className="relative">
      <span className="text-ink-4" aria-hidden="true">—</span>
      <span className="sr-only">None</span>
    </span>
  );
}

export function SubmissionLogTab({
  allRows,
  rows,
  viewRows,
  counts,
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  loading,
  isAdmin,
  onOpenDrawing,
  onFixDate,
  onRecordReply,
  onMarkImportAwaiting,
  onLogTransmittal,
  onExport,
  rowMenuItems,
  chaseRows,
  onChase,
  currentUserName = '',
  submittedNotLogged = 0,
  onShowSubmittedNotLogged,
  readOnly = false,
}) {
  const set = (patch) => onFiltersChange({ ...filters, ...patch });
  const canChase = !readOnly && Boolean(onChase);
  const [chaseOpen, setChaseOpen] = useState(false);
  const [chaseOne, setChaseOne] = useState(null);
  const [turnaroundOpen, setTurnaroundOpen] = useState(false);
  // Mailbox suggestions are there to be confirmed (a write): a read-only role never loads them.
  const inbox = useReplyInbox(!loading && !readOnly);
  const oneContacts = useChaseContacts(Boolean(chaseOne));
  const overdueCount = useMemo(() => chaseGroups(chaseRows || []).reduce((sum, group) => sum + group.rows.length, 0), [chaseRows]);
  const chaseRow = useCallback((row) => setChaseOne({
    key: row.key,
    project: row.drawing.project || 'No project',
    projectNo: String(row.drawing.project_no || '').replace(/^#/, ''),
    recipient: row.to || 'Consultant',
    rows: [row],
  }), []);
  const view = replyViewOf(filters, counts);
  const options = useMemo(() => ({
    project: optionCounts(allRows, (row) => row.drawing.project),
    disc: optionCounts(allRows, (row) => row.drawing.disc),
    by: optionCounts(allRows, (row) => row.by, { labelOf: displayPerson }),
    bucket: SENT_BUCKETS.map((bucket) => ({ ...bucket })),
  }), [allRows]);
  // Aging chips count the rows of the current reply view (before the age chip itself).
  const ageCounts = useMemo(() => {
    const result = {};
    viewRows.forEach((row) => {
      const bucket = ageBucket(row.sent);
      result[bucket] = (result[bucket] || 0) + 1;
    });
    return result;
  }, [viewRows]);
  const anyFilter = Boolean(filters.project || filters.disc || filters.by || filters.bucket || filters.search || filters.age);
  const [selectedKeys, setSelectedKeys] = useState([]);

  /* transmittals */
  const infoByKey = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      if (!map.has(row.transmittalKey)) map.set(row.transmittalKey, []);
      map.get(row.transmittalKey).push(row);
    });
    return new Map([...map.entries()].map(([key, list]) => [key, { ...transmittalInfo(list), rows: list }]));
  }, [rows]);
  const flagged = rows.filter((row) => row.sent.needsCheck);
  const flaggedTransmittals = new Set(flagged.map((row) => row.transmittalKey)).size;
  const groupBy = useCallback((row) => ({ key: row.transmittalKey, label: infoByKey.get(row.transmittalKey)?.project || row.drawing.project }), [infoByKey]);
  const sortGroups = useCallback((a, b) => {
    const left = infoByKey.get(a.key);
    const right = infoByKey.get(b.key);
    if (left?.flagged !== right?.flagged) return left?.flagged ? -1 : 1;
    const x = left?.sent?.iso || '';
    const y = right?.sent?.iso || '';
    if (x !== y) {
      if (!x) return 1;
      if (!y) return -1;
      return (x < y ? -1 : 1) * (sort === 'desc' ? -1 : 1);
    }
    return String(left?.project).localeCompare(String(right?.project));
  }, [infoByKey, sort]);
  const groupOrder = useMemo(() => {
    const keys = [...infoByKey.keys()];
    return keys.map((key) => ({ key })).sort(sortGroups).map((item) => ({ key: item.key, count: infoByKey.get(item.key).count }));
  }, [infoByKey, sortGroups]);

  const viewKey = JSON.stringify([filters, sort, view]);
  const [collapsedState, setCollapsedState] = useState({ key: null, value: [] });
  const collapsed = useMemo(() => {
    if (collapsedState.key === viewKey) return collapsedState.value;
    // Open the first transmittals (~30 rows). On a long list the ones that need a date fix start
    // closed (the strip above says so and opens them), so the oldest real wait is on screen first.
    const budget = anyFilter ? 100 : DEFAULT_OPEN_ROWS;
    const skipFlagged = rows.length > budget;
    const open = new Set();
    let used = 0;
    for (const group of groupOrder) {
      if (skipFlagged && infoByKey.get(group.key)?.flagged) continue;
      if (open.size && used + group.count > budget) break;
      open.add(group.key);
      used += group.count;
    }
    return groupOrder.filter((group) => !open.has(group.key)).map((group) => group.key);
  }, [collapsedState, viewKey, groupOrder, anyFilter, rows.length, infoByKey]);
  const setCollapsed = useCallback((value) => setCollapsedState({ key: viewKey, value }), [viewKey]);

  const flaggedClosed = collapsed.filter((key) => infoByKey.get(key)?.flagged);

  const selectedRows = useMemo(() => {
    const keys = new Set(selectedKeys);
    return rows.filter((row) => keys.has(row.key));
  }, [rows, selectedKeys]);

  const SortIcon = sort === 'desc' ? ArrowDown : ArrowUp;
  const columns = useMemo(() => [
    { key: 'drawing', header: 'Drawing', hideable: false, truncate: true, minWidth: 220, render: (row) => <SubmissionDrawingCell row={row} /> },
    { key: 'disc', header: 'Disc.', title: 'Discipline', width: 70, render: (row) => <DisciplineChip disc={row.drawing.disc} /> },
    { key: 'block', header: 'Block', width: 96, truncate: true, render: (row) => (row.drawing.block ? <span className="text-[12.5px] text-ink-2" title={row.drawing.block}>{row.drawing.block}</span> : <Dash />) },
    {
      key: 'sent',
      header: (
        <button
          type="button"
          onClick={() => onSortChange(sort === 'desc' ? 'asc' : 'desc')}
          className="-mx-1 inline-flex items-center gap-1 rounded px-1 font-bold text-ink-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title={sort === 'desc' ? 'Newest first — click for oldest first' : 'Oldest first — click for newest first'}
          data-testid="submission-log-sort"
        >
          Sent<SortIcon className="h-3 w-3" aria-hidden="true" />
        </button>
      ),
      title: 'Sent',
      headerClassName: '',
      width: 156,
      render: (row) => <SentCell row={row} onFixDate={readOnly ? null : onFixDate} />,
    },
    {
      key: 'by', header: 'By', width: 60,
      render: (row) => (row.by ? (
        // `relative` keeps the sr-only text inside the cell (see Dash).
        <span title={`Sent by ${row.by}`} className="relative inline-flex"><PersonAvatar name={row.by} size={24} /><span className="sr-only">Sent by {row.by}</span></span>
      ) : <Dash />),
    },
    { key: 'reply', header: 'Reply', width: 185, truncate: true, render: (row) => <ReplyCell row={row} /> },
    {
      key: 'actions', header: '', width: 176, hideable: false, cellClassName: 'pl-1',
      render: (row) => (
        <div className="flex items-center justify-end gap-0.5">
          {readOnly ? null : row.replyState !== 'replied' ? (
            <RecordReplyPopover rows={[row]} onSave={(form) => onRecordReply([row], form)} testId={`record-reply-${row.key}`} className="gap-1 px-2" />
          ) : (
            <RecordReplyPopover rows={[row]} onSave={(form) => onRecordReply([row], form)} label="Change" variant="ghost" title="Change the recorded reply" testId={`record-reply-${row.key}`} className="gap-1 px-2 text-ink-3" />
          )}
          {canChase && row.replyState === 'awaiting' ? (
            <IconAction icon={Mail} label={`Chase the reply for ${drawingTitle(row.drawing)}`} title="Chase" onClick={(event) => { event.stopPropagation(); chaseRow(row); }} data-testid={`chase-row-${row.key}`} />
          ) : null}
          <RowActions label={`More actions for ${drawingTitle(row.drawing)}`} items={rowMenuItems(row.drawing)} size="sm" />
        </div>
      ),
    },
  ], [canChase, chaseRow, onFixDate, onRecordReply, onSortChange, readOnly, rowMenuItems, sort]);

  const segOptions = [
    { key: 'awaiting', label: 'Awaiting reply', count: counts.awaiting, testId: 'log-view-awaiting' },
    { key: 'replied', label: 'Replied', count: counts.replied, testId: 'log-view-replied' },
    counts.not_recorded ? { key: 'not_recorded', label: 'Not recorded', count: counts.not_recorded, testId: 'log-view-not-recorded', title: 'The Excel import said Approved, but the drawing is not approved — nobody recorded the real reply' } : null,
    // Every count here is of the filtered list (search + chips), so the four add up.
    { key: 'all', label: 'All', count: counts.all, testId: 'log-view-all' },
  ].filter(Boolean);

  return (
    <div className="space-y-3" data-testid="submission-log-tab">
      <ImportBanner
        allRows={allRows}
        isAdmin={isAdmin}
        readOnly={readOnly}
        onMarkAwaiting={onMarkImportAwaiting}
        onShowNotRecorded={() => set({ reply: 'not_recorded', age: null })}
      />
      {readOnly ? null : <ReplyInbox inbox={inbox} rows={allRows} onRecordReply={onRecordReply} />}

      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Which submissions"
          value={view}
          onChange={(key) => set({ reply: key, age: null })}
          options={segOptions}
          testId="log-reply-view"
        />
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Waiting time">
          {AGE_BUCKETS.filter((bucket) => ageCounts[bucket.value]).map((bucket) => (
            <AgeChip
              key={bucket.value}
              bucket={bucket}
              count={ageCounts[bucket.value]}
              active={filters.age === bucket.value}
              onClick={() => set({ age: filters.age === bucket.value ? null : bucket.value })}
            />
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-ink-2"
            onClick={() => setTurnaroundOpen(true)}
            disabled={loading}
            aria-label="Consultant turnaround"
            title="Consultant turnaround: reply times and waiting per project"
            data-testid="turnaround-btn"
          >
            <Gauge aria-hidden="true" />
          </Button>
          {canChase ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setChaseOpen(true)} disabled={loading || !overdueCount} title={overdueCount ? 'One follow-up email per project and consultant' : 'Nothing waits longer than 90 days'} data-testid="chase-overdue-btn">
              <Mail aria-hidden="true" /> {overdueCount ? `Chase ${fmt(overdueCount)} overdue` : 'Chase overdue'}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchField
          value={filters.search}
          onChange={(value) => set({ search: value })}
          placeholder="Search drawing or ID"
          label="Search submissions"
          testId="submission-log-search"
          className="w-full sm:w-[216px]"
        />
        <ChipSelect label="Project" value={filters.project} options={options.project} onChange={(value) => set({ project: value })} testId="log-filter-project" />
        <ChipSelect label="Discipline" value={filters.disc} options={options.disc} onChange={(value) => set({ disc: value })} testId="log-filter-discipline" />
        <ChipSelect label="Sent by" value={filters.by} options={options.by} onChange={(value) => set({ by: value })} testId="log-filter-sent-by" />
        <ChipSelect label="Date sent" value={filters.bucket} options={options.bucket} onChange={(value) => set({ bucket: value })} testId="log-filter-date-sent" />
        {/* 15 mockup: "169 of 359 · 31 transmittals · 68 Submitted not logged ↗" on the toolbar line (the order is on
            the Sent header and in the footer). */}
        <span className="ml-auto whitespace-nowrap text-[12.5px] text-ink-3" data-testid="submission-log-count">
          <b className="font-semibold text-ink-2">{fmt(rows.length)}</b> of {fmt(allRows.length)}<span className="sr-only"> submissions</span>
          {' · '}<b className="font-semibold text-ink-2">{fmt(infoByKey.size)}</b> {infoByKey.size === 1 ? 'transmittal' : 'transmittals'}
          {submittedNotLogged && onShowSubmittedNotLogged ? (
            <>
              {' · '}
              <button
                type="button"
                onClick={onShowSubmittedNotLogged}
                className="inline-flex items-center gap-0.5 rounded font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title="Opens All Drawings filtered to Submitted drawings with no submission logged"
                data-testid="submitted-not-logged-link"
              >
                {fmt(submittedNotLogged)} Submitted not logged<ArrowUpRight className="h-3 w-3" aria-hidden="true" />
              </button>
            </>
          ) : null}
        </span>
        {readOnly ? null : (
          <Button type="button" variant="outline" size="sm" onClick={onLogTransmittal} data-testid="log-transmittal-btn">
            <Send aria-hidden="true" /> Log transmittal
          </Button>
        )}
      </div>

      {!loading && flagged.length ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-xl border border-[color:rgb(217_119_6/0.28)] bg-status-amber-bg px-3.5 py-2 text-[12.5px] text-ink-2" data-testid="submission-log-needs-check">
          <TriangleAlert className="h-3.5 w-3.5 text-status-amber-ink" aria-hidden="true" />
          <b className="text-[13px] font-bold text-ink">Needs a date fix</b>
          <span>
            · {fmt(flagged.length)} {flagged.length === 1 ? 'submission' : 'submissions'} in {fmt(flaggedTransmittals)} {flaggedTransmittals === 1 ? 'transmittal' : 'transmittals'} with a date in the future, a month without a day, or no readable date. They are listed first{readOnly ? '' : '; use Fix on each row'}.
          </span>
          {flaggedClosed.length ? (
            <Button type="button" variant="outline" size="xs" className="ml-auto" onClick={() => setCollapsed(collapsed.filter((key) => !flaggedClosed.includes(key)))} data-testid="open-date-fixes">
              Open {flaggedClosed.length === 1 ? 'it' : 'them'}
            </Button>
          ) : null}
        </div>
      ) : null}

      <DataTable
        testId="submission-log-table"
        ariaLabel="Submission log"
        columns={columns}
        rows={rows}
        rowKey="key"
        loading={loading}
        skeletonRows={8}
        manualSort
        selectable={!readOnly}
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        getRowLabel={(row) => drawingTitle(row.drawing)}
        onRowOpen={(row) => onOpenDrawing(row.drawing)}
        rowTestId={(row) => `submission-row-${row.key}`}
        rowClassName={(row) => (row.sent.needsCheck ? '[&>td]:bg-[color:rgb(217_119_6/0.04)]' : undefined)}
        groupBy={groupBy}
        sortGroups={sortGroups}
        collapsedGroups={collapsed}
        onCollapsedGroupsChange={setCollapsed}
        renderGroupHeader={(group) => <TransmittalTitle info={infoByKey.get(group.key)} />}
        // The title already says "3 LCS drawings": no count pill after it.
        groupCount={() => null}
        groupMeta={(group) => {
          const info = infoByKey.get(group.key);
          if (!info) return null;
          const waiting = info.rows.filter((row) => row.replyState !== 'replied');
          return (
            <>
              {waiting.length > 1 && !readOnly ? (
                <RecordReplyPopover
                  rows={waiting}
                  onSave={(form) => onRecordReply(waiting, form)}
                  label={`Reply to ${fmt(waiting.length)}`}
                  variant="ghost"
                  title="Record the reply for this transmittal"
                  testId={`record-reply-group-${group.key}`}
                  className="hidden h-7 text-ink-2 sm:inline-flex"
                />
              ) : null}
              {info.awaiting && info.waitingDays !== null ? (
                <AgeText days={info.waitingDays} suffix=" waiting" className="min-w-[96px] text-right text-[12.5px] font-bold" />
              ) : info.awaiting ? (
                <span className="text-[12.5px] font-semibold text-status-amber-ink">awaiting reply</span>
              ) : info.notRecorded ? (
                <span className="text-[12.5px] font-semibold text-ink-3">not recorded</span>
              ) : (
                <span className="text-[12.5px] font-semibold text-ink-3">replied</span>
              )}
            </>
          );
        }}
        groupRowLimit={25}
        pagingKey={viewKey}
        footer={(
          <TableFoot
            className="rounded-b-card"
            testId="submission-log-footer"
            left={(
              <span>
                {rows.length
                  ? <><b className="font-semibold text-ink-2">{fmt(rows.length)}</b> {rows.length === 1 ? 'submission' : 'submissions'} in <b className="font-semibold text-ink-2">{fmt(infoByKey.size)}</b> {infoByKey.size === 1 ? 'transmittal' : 'transmittals'} · {{ awaiting: 'awaiting reply', replied: 'replied', not_recorded: 'reply not recorded', all: 'all replies' }[view]} · {sort === 'desc' ? 'newest first' : 'oldest first'}</>
                  : 'No submissions'}
              </span>
            )}
          >
            <Button type="button" variant="ghost" size="sm" onClick={onExport} disabled={!rows.length} data-testid="export-submission-log-btn">
              <Download aria-hidden="true" /> Export this view
            </Button>
          </TableFoot>
        )}
        empty={(
          <EmptyState
            icon={SearchX}
            titleAs="p"
            title={allRows.length ? (anyFilter ? 'No submissions match these filters' : `Nothing in “${segOptions.find((option) => option.key === view)?.label}”`) : 'No submissions logged yet'}
            text={allRows.length ? 'Try another view or clear the filters.' : readOnly ? 'Logged submissions show up here.' : 'Use Log transmittal, or select drawings on All Drawings and use Log submission.'}
            action={anyFilter ? <Button type="button" variant="outline" size="sm" onClick={() => onFiltersChange({ ...EMPTY_LOG_FILTERS, reply: filters.reply })}>Clear filters</Button> : null}
          />
        )}
        renderBulkBar={readOnly ? undefined : ({ count, clear }) => (
          <BulkBar count={count} onClear={clear} testId="submission-log-bulkbar" label={count === 1 ? 'submission selected' : 'submissions selected'}>
            <RecordReplyPopover
              rows={selectedRows}
              onSave={async (form) => { await onRecordReply(selectedRows, form); setSelectedKeys([]); }}
              label={`Record reply (${fmt(selectedRows.length)})`}
              variant="ghost"
              align="center"
              testId="submission-log-bulk-reply"
              className="h-[30px] border-0 bg-white/10 px-2.5 text-[12.5px] font-semibold text-white hover:bg-white/20 hover:text-white"
            />
          </BulkBar>
        )}
      />

      {canChase ? (
        <>
          <ChaseDialog open={chaseOpen} onOpenChange={setChaseOpen} rows={chaseRows || []} currentUserName={currentUserName} onChase={onChase} />
          <ChaseComposer group={chaseOne} onClose={() => setChaseOne(null)} contacts={oneContacts} currentUserName={currentUserName} onChase={onChase} />
        </>
      ) : null}
      <TurnaroundPanel
        open={turnaroundOpen}
        onOpenChange={setTurnaroundOpen}
        rows={allRows}
        onPickProject={(project) => { setTurnaroundOpen(false); set({ project: valueKey(project), reply: null, age: null }); }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ banner */

function ImportBanner({ allRows, isAdmin, readOnly = false, onMarkAwaiting, onShowNotRecorded }) {
  const imported = allRows.filter((row) => row.fromImport);
  const unfixed = imported.filter((row) => row.importUnfixed);
  const notRecorded = imported.filter((row) => row.replyState === 'not_recorded');
  const fixedAwaiting = imported.filter((row) => row.replyState === 'awaiting' && !row.importUnfixed);
  const signature = unfixed.length ? `fix:${unfixed.length}` : notRecorded.length ? `nr:${notRecorded.length}` : '';
  const [dismissed, setDismissed] = useState(() => readStored(BANNER_KEY, ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!signature || dismissed === signature) return null;
  const dismiss = () => {
    writeStored(BANNER_KEY, signature);
    setDismissed(signature);
  };

  if (unfixed.length) {
    const mark = async () => {
      setBusy(true);
      setError('');
      try {
        await onMarkAwaiting(unfixed.length);
      } catch (err) {
        setError(getErrorMessage(err, 'Could not update the replies'));
      } finally {
        setBusy(false);
      }
    };
    return (
      <Callout
        tone="amber"
        icon={TriangleAlert}
        testId="import-replies-banner"
        title={<b className="font-bold">Replies were filled in automatically by the Excel import</b>}
        sub={(
          <>
            {fmt(imported.length)} submissions say “Approved · Sent to Consultant” with the note “Imported from Submitted = YES”, but {fmt(unfixed.length)} of those drawings are still Submitted. They are shown below as Awaiting reply.
            {error ? <span className="mt-1 block font-semibold text-status-red-ink" role="alert">{error}</span> : null}
            {!isAdmin ? <span className="mt-1 block text-ink-3">An Admin can store them as Awaiting reply.</span> : null}
          </>
        )}
        actions={(
          <>
            {isAdmin && !readOnly ? (
              <Button type="button" size="sm" variant="outline" onClick={mark} disabled={busy} data-testid="mark-import-awaiting-btn">
                {busy ? 'Saving…' : `Mark ${fmt(unfixed.length)} as awaiting reply`}
              </Button>
            ) : null}
            <Button type="button" size="sm" variant="ghost" onClick={dismiss} data-testid="import-banner-keep">Keep as is</Button>
          </>
        )}
      />
    );
  }

  return (
    <Callout
      tone="amber"
      icon={TriangleAlert}
      testId="import-replies-banner"
      title={<b className="font-bold">{fmt(notRecorded.length)} replies from the Excel import were never recorded</b>}
      sub={`The import marked every submitted drawing “Approved”. ${fixedAwaiting.length ? `${fmt(fixedAwaiting.length)} drawings that are still Submitted now wait for a reply; ` : ''}${fmt(notRecorded.length)} on drawings that are Blocked, In progress or Not started show as Not recorded until someone records the real reply.`}
      actions={(
        <>
          <Button type="button" size="sm" variant="outline" onClick={onShowNotRecorded} data-testid="show-not-recorded-btn">Show the {fmt(notRecorded.length)}</Button>
          <Button type="button" size="icon-sm" variant="ghost" onClick={dismiss} aria-label="Dismiss" title="Dismiss" data-testid="import-banner-dismiss"><X aria-hidden="true" /></Button>
        </>
      )}
    />
  );
}

/* ------------------------------------------------------------------ cells */

function AgeChip({ bucket, count, active, onClick }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      data-testid={`log-age-${bucket.value}`}
      className={cn(
        'inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-full border px-[11px] text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'border-[color:var(--brand-soft-border)] bg-brand-soft text-[color:var(--brand-ink)]' : 'border-line bg-surface text-ink-2 hover:bg-surface-hover hover:text-ink'
      )}
    >
      {bucket.tone === 'warn' ? (
        <TriangleAlert className="h-3 w-3 text-status-amber-ink" aria-hidden="true" />
      ) : (
        <span className={cn('ui-tone-dot', `tone-${bucket.tone}`)} style={{ width: 7, height: 7 }} aria-hidden="true" />
      )}
      {bucket.label}
      <span className={cn('tabular-nums', active ? 'font-bold' : 'font-semibold text-ink-3')}>{fmt(count)}</span>
    </button>
  );
}

function TransmittalTitle({ info }) {
  if (!info) return null;
  const sent = info.sent?.ok ? info.sent.label : (info.sent?.raw || 'no date');
  return (
    <GroupTitle
      label={info.project}
      number={info.projectNo}
      noNumberText="no project no."
      meta={`· ${transmittalNoun(info)} · sent ${sent}${info.by ? ` by ${info.by}` : ''}`}
    >
      {info.flagged ? (
        <span className="inline-flex h-[18px] shrink-0 items-center gap-1 rounded-[5px] bg-status-amber-bg px-1.5 text-[10.5px] font-bold text-status-amber-ink">
          <TriangleAlert className="h-3 w-3" aria-hidden="true" /> Check date
        </span>
      ) : null}
      {info.raws.length > 1 ? (
        <span className="hidden h-[18px] shrink-0 items-center rounded-[5px] border border-line px-1.5 font-mono text-[10.5px] font-medium text-ink-3 xl:inline-flex" title="Dates typed in different ways that are the same day">
          merged: {info.raws.slice(0, 3).join(' + ')}
        </span>
      ) : null}
    </GroupTitle>
  );
}

function SubmissionDrawingCell({ row }) {
  const { drawing, submission } = row;
  const title = drawingTitle(drawing);
  const pdf = safeHref(submission.pdf);
  // 15 #6: the Drawing ID tells which drawing of a transmittal was sent, so it has priority: the internal SD number
  // gives way first, then the ID keeps its unique end ("…-PL-LI-22013"). Rev and PDF show as small tags when present.
  return (
    <div className="min-w-0 py-0.5">
      <span className="block truncate text-[13px] font-semibold text-ink" title={`${title} · ${drawing.id}`}>{title}</span>
      <span className="mt-0.5 flex min-w-0 items-center gap-1.5 whitespace-nowrap text-[11.5px] text-ink-3">
        <DrawingIdText value={drawing.drawingId} keepEnd className="min-w-0 max-w-full shrink-0" />
        <SubLineExtras className="[&>*:nth-child(n+3)]:ml-1">
          {submission.rev ? <span className="rounded border border-line px-1 font-mono text-[10.5px] leading-[16px] text-ink-2" title="Revision sent">{submission.rev}</span> : null}
          {pdf ? (
            <a
              href={pdf}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              className="grid h-[18px] w-[18px] place-items-center rounded text-brand-ink hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              title="PDF sent"
              aria-label="Open the PDF sent"
              data-testid={`submission-pdf-${row.key}`}
            >
              <FileText className="h-3 w-3" aria-hidden="true" />
            </a>
          ) : null}
          <SubLinePart className="font-mono text-ink-4" title={drawing.id}>{drawing.id}</SubLinePart>
        </SubLineExtras>
      </span>
    </div>
  );
}

function ReplyCell({ row }) {
  const code = REPLY_CODE_OF[row.reply];
  if (row.replyState === 'replied') {
    const item = REPLY_CODES.find((entry) => entry.code === code);
    return (
      <div className="min-w-0">
        <span className="inline-flex max-w-full items-center gap-1.5">
          {item ? <ReplyCodeBadge code={item.code} tone={item.tone} className="h-[20px] w-[20px] text-[10.5px]" /> : null}
          <StatusPill status={row.reply} domain="consultant_reply" tone={getStatusTone(row.reply, 'consultant_reply')} label={labelize(row.reply, REPLY_LABELS)} />
        </span>
        {row.replyDate ? (
          <span className={cn(subClass, 'truncate')} title={row.replyComments || undefined}>replied {row.replyDate.label}{row.replyComments ? ` · ${row.replyComments}` : ''}</span>
        ) : row.comments ? (
          <span className={cn(subClass, 'truncate')} title={row.comments}>{row.comments}</span>
        ) : row.fromImport ? (
          <span className={subClass}>from the Excel import</span>
        ) : null}
      </div>
    );
  }
  const chasedAt = row.submission.chased_at;
  return (
    <div className="min-w-0">
      <StatusPill tone={row.replyState === 'awaiting' ? 'amber' : 'outline'} label={replyLabel(row)} />
      {chasedAt ? (
        <span className={cn(subClass, 'truncate text-ink-2')} title={`Chased ${row.submission.chase_count || 1}× · last ${formatDate(chasedAt)}${row.submission.chased_by ? ` by ${row.submission.chased_by}` : ''}`} data-testid={`chased-${row.key}`}>
          <MailCheck className="mr-1 inline h-3 w-3 align-[-2px]" aria-hidden="true" />chased {formatDate(chasedAt, { year: 'auto' })}{row.submission.chase_count > 1 ? ` · ${row.submission.chase_count}×` : ''}
        </span>
      ) : row.importReply ? (
        <span className={cn(subClass, 'text-ink-3')} title="The Excel import filled this reply in; nobody recorded the consultant's real reply">
          import said {labelize(row.importReply, REPLY_LABELS)}
        </span>
      ) : row.comments ? (
        <span className={cn(subClass, 'truncate')} title={row.comments}>{row.comments}</span>
      ) : null}
    </div>
  );
}

function SentCell({ row, onFixDate }) {
  const { sent } = row;
  const recipient = row.to && row.to.toLowerCase() !== 'consultant' ? row.to : '';
  if (sent.needsCheck) {
    return (
      <div className="min-w-0">
        <span className="block truncate font-semibold text-ink" title={sent.raw ? `Typed as “${sent.raw}”` : undefined}>{sent.label}</span>
        <span className="mt-0.5 flex items-center gap-1.5 whitespace-nowrap">
          <span className="inline-flex h-[18px] items-center gap-1 rounded-[5px] bg-status-amber-bg px-1.5 text-[10.5px] font-bold text-status-amber-ink" title={sent.reason}>
            <TriangleAlert className="h-3 w-3" aria-hidden="true" /> Check date
          </span>
          {onFixDate ? <FixDateButton row={row} onFixDate={onFixDate} /> : null}
        </span>
      </div>
    );
  }
  return (
    <div className="min-w-0">
      <span className="block font-semibold text-ink">{sent.label}</span>
      <span className={cn(subClass, 'truncate')}>
        <AgeText days={sent.age} suffix=" ago" className="font-semibold" />
        {recipient ? <span className="text-ink-3"> · to {recipient}</span> : null}
      </span>
    </div>
  );
}

function FixDateButton({ row, onFixDate }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const max = todayIso();

  const onOpenChange = (next) => {
    setOpen(next);
    if (next) {
      // Prefill only a complete date we can stand behind: the day/month swap of a future date.
      // A month-only date ('JULY.2025') stays empty — its day is unknown, so the person picks it.
      setValue(row.sent.suggestion?.iso || '');
      setError('');
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (!value) return;
    setSaving(true);
    setError('');
    try {
      await onFixDate(row, toDayFirst(value));
      setOpen(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the date'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          className="inline-flex h-[18px] items-center gap-1 rounded-[5px] border border-line bg-surface px-1.5 text-[10.5px] font-bold text-ink-2 hover:bg-surface-hover hover:text-ink"
          data-testid={`fix-date-${row.key}`}
        >
          <CalendarClock className="h-3 w-3" aria-hidden="true" /> Fix
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 rounded-xl border-line bg-surface p-3.5 text-ink shadow-overlay"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <form onSubmit={save} className="space-y-2.5">
          <div>
            <p className="text-[13px] font-bold text-ink">Fix date sent</p>
            <p className="mt-0.5 text-[12px] text-ink-3">
              {drawingTitle(row.drawing)} · typed as <span className="font-mono text-ink-2">“{row.sent.raw || 'nothing'}”</span>. {row.sent.reason}.
            </p>
          </div>
          {row.sent.suggestion ? (
            <p className="rounded-lg bg-brand-soft px-2.5 py-1.5 text-[12px] text-ink-2">
              Suggested <b className="font-semibold text-ink">{row.sent.suggestion.label}</b>: the same date with day and month swapped.
            </p>
          ) : row.sent.monthOnly ? (
            <p className="rounded-lg bg-surface-2 px-2.5 py-1.5 text-[12px] text-ink-2" data-testid={`fix-date-month-hint-${row.key}`}>
              Only the month is known (<b className="font-semibold text-ink">{row.sent.label}</b>). Pick the day it was sent.
            </p>
          ) : null}
          <label className="block text-[12px] font-semibold text-ink-2">
            Date sent
            <input
              type="date"
              value={value}
              max={max}
              onChange={(event) => setValue(event.target.value)}
              required
              className="mt-1 h-9 w-full rounded-control border border-line bg-surface px-2.5 text-[13px] text-ink outline-none focus:border-[color:var(--brand-ink)] focus:ring-2 focus:ring-[color:var(--brand-soft-border)]"
              data-testid={`fix-date-input-${row.key}`}
            />
          </label>
          {error ? <p className="text-[12px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" size="sm" disabled={!value || saving} data-testid={`fix-date-save-${row.key}`}>{saving ? 'Saving…' : 'Save date'}</Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
